import React, { useMemo, useEffect } from "react";
import {
  FiCalendar,
  FiClock,
  FiPlus,
  FiChevronDown,
  FiEdit2,
  FiX,
} from "react-icons/fi";
import { Popover, Transition } from "@headlessui/react";
import { DayPicker } from "react-day-picker";
import { format } from "date-fns";

// ✅ เวลาออฟฟิศ (ปรับได้)
const OFFICE_START = 9 * 60; // 09:00
const OFFICE_END = 18 * 60; // 18:00

const pad2 = (n) => String(n).padStart(2, "0");
const cx = (...s) => s.filter(Boolean).join(" ");

const timeToMin = (t) => {
  if (!t) return 0;
  const [hh, mm] = t.split(":").map(Number);
  return hh * 60 + mm;
};
const minToTime = (m) => `${pad2(Math.floor(m / 60))}:${pad2(m % 60)}`;

const buildTimeSlots = (startMin, endMin, step = 30) => {
  const out = [];
  for (let m = startMin; m <= endMin; m += step) out.push(minToTime(m));
  return out;
};

const BookingForm = ({
  selectedRoom,
  date,
  setDate,
  startTime,
  setStartTime,
  endTime,
  setEndTime,
  purpose,
  setPurpose,
  onSubmit,

  // ✅ เพิ่มให้รองรับ edit mode
  editingBooking,
  onCancelEdit,
}) => {
  const isEdit = !!editingBooking;

  // ✅ สร้าง slot เวลาเริ่ม (09:00 - 17:30)
  const startSlots = useMemo(() => {
    return buildTimeSlots(OFFICE_START, OFFICE_END - 30, 30);
  }, []);

  // ✅ เวลา end ต้องมากกว่า start อย่างน้อย 30 นาที
  const endSlots = useMemo(() => {
    const s = timeToMin(startTime || minToTime(OFFICE_START));
    const minEnd = s + 30;
    return buildTimeSlots(minEnd, OFFICE_END, 30);
  }, [startTime]);

  // ✅ parse date string -> Date object
  const dateObj = useMemo(() => {
    if (!date) return undefined;
    const [y, m, d] = date.split("-").map(Number);
    return new Date(y, m - 1, d);
  }, [date]);

  // ✅ auto set endTime ถ้า end <= start
  useEffect(() => {
    if (!startTime) return;
    const s = timeToMin(startTime);
    const e = timeToMin(endTime);
    if (!endTime || e <= s) {
      const next = Math.min(s + 30, OFFICE_END);
      setEndTime(minToTime(next));
    }
  }, [startTime, endTime, setEndTime]);

  const durations = [30, 60, 90, 120];
  const applyDuration = (mins) => {
    if (!startTime) return;
    const s = timeToMin(startTime);
    const next = Math.min(s + mins, OFFICE_END);
    setEndTime(minToTime(next));
  };

  const TimeSlotPicker = ({ label, value, onChange, slots, hint }) => (
    <div className="min-w-0">
      <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">
        {label}
      </label>

      <div className="relative">
        <FiClock className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-400" />
        <select
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className={cx(
            "w-full min-w-0 appearance-none rounded-xl border py-3 pl-10 pr-10",
            "bg-white dark:bg-slate-950 border-gray-200 dark:border-slate-800",
            "text-base font-semibold text-gray-800 dark:text-slate-100",
            "focus:outline-none focus:ring-2 focus:ring-indigo-500/70 dark:focus:ring-indigo-400/60",
          )}
        >
          <option value="" disabled>
            เลือกเวลา
          </option>
          {slots.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        <FiChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-400" />
      </div>

      {hint && (
        <p className="mt-1 text-xs leading-5 text-gray-400 dark:text-slate-500">
          {hint}
        </p>
      )}
    </div>
  );

  return (
    <div className="bg-white dark:bg-slate-900 rounded-2xl shadow dark:shadow-black/30 p-4 md:p-6 border border-transparent dark:border-slate-800">
      <div className="flex flex-col gap-2 mb-4 sm:flex-row sm:items-start sm:justify-between">
        <h2 className="min-w-0 font-semibold text-gray-800 dark:text-slate-100 flex items-center gap-2">
          {isEdit ? (
            <>
              <FiEdit2 className="text-gray-700 dark:text-slate-200" />
              แก้ไขการจอง
            </>
          ) : (
            <>
              <FiPlus className="text-gray-700 dark:text-slate-200" />
              สร้างรายการจอง
            </>
          )}
        </h2>

        {selectedRoom && (
          <div className="text-xs text-gray-500 dark:text-slate-400 sm:text-right">
            ห้องที่เลือก:{" "}
            <span className="font-medium text-gray-700 dark:text-slate-200">
              ชั้น {selectedRoom.floor} •{" "}
              {selectedRoom.capacityLabel || selectedRoom.capacity} คน
            </span>
          </div>
        )}
      </div>

      {/* ✅ Banner เล็ก ๆ บอกว่าอยู่ในโหมดแก้ไข */}
      {isEdit && (
        <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-400/30 dark:bg-amber-500/10 dark:text-amber-200">
          กำลังแก้ไขรายการ:{" "}
          <span className="font-medium">
            {editingBooking?.startTime} - {editingBooking?.endTime}
          </span>
        </div>
      )}

      <form onSubmit={onSubmit} className="space-y-4">
        {/* Date */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">
            วันที่
          </label>

          <Popover className="relative">
            {({ open, close }) => (
              <>
                <Popover.Button
                  className={cx(
                    "w-full rounded-xl border px-3 py-3",
                    "bg-white dark:bg-slate-950 border-gray-200 dark:border-slate-800",
                    "focus:outline-none focus:ring-2 focus:ring-indigo-500/70 dark:focus:ring-indigo-400/60",
                    "flex items-center justify-between gap-3",
                  )}
                >
                  <div className="flex items-center gap-2 text-gray-800 dark:text-slate-100">
                    <FiCalendar className="text-gray-400 dark:text-slate-400" />
                    <span className="font-medium">
                      {dateObj ? format(dateObj, "dd/MM/yyyy") : "เลือกวันที่"}
                    </span>
                  </div>
                  <FiChevronDown
                    className={cx(
                      "text-gray-400 transition",
                      open && "rotate-180",
                    )}
                  />
                </Popover.Button>

                <Transition
                  enter="transition ease-out duration-120"
                  enterFrom="opacity-0 translate-y-1"
                  enterTo="opacity-100 translate-y-0"
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100 translate-y-0"
                  leaveTo="opacity-0 translate-y-1"
                >
                  <Popover.Panel
                    className={cx(
                      "absolute z-30 mt-2 rounded-2xl border",
                      "bg-white dark:bg-slate-950 border-gray-200 dark:border-slate-800",
                      "shadow-lg dark:shadow-black/40 p-3",
                    )}
                  >
                    <DayPicker
                      mode="single"
                      selected={dateObj}
                      onSelect={(d) => {
                        if (!d) return;
                        const y = d.getFullYear();
                        const m = pad2(d.getMonth() + 1);
                        const dd = pad2(d.getDate());
                        setDate(`${y}-${m}-${dd}`);
                        close();
                      }}
                      weekStartsOn={1}
                      showOutsideDays
                      className="text-sm"
                      classNames={{
                        months: "flex flex-col",
                        month: "space-y-2",
                        caption: "flex items-center justify-between px-1",
                        caption_label:
                          "text-sm font-semibold text-gray-800 dark:text-slate-100",
                        nav: "flex items-center gap-2",
                        nav_button:
                          "h-8 w-8 inline-flex items-center justify-center rounded-lg border border-gray-200 dark:border-slate-800 hover:bg-gray-50 dark:hover:bg-slate-900 text-gray-700 dark:text-slate-200",
                        table: "w-full border-collapse",
                        head_row: "flex",
                        head_cell:
                          "w-9 text-center text-[11px] font-medium text-gray-500 dark:text-slate-400",
                        row: "flex w-full mt-1",
                        cell: "w-9 h-9 text-center",
                        day: "w-9 h-9 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-900 text-gray-700 dark:text-slate-200",
                        day_selected:
                          "bg-indigo-600 text-white hover:bg-indigo-600",
                        day_today: "border border-indigo-400/60",
                        day_outside: "text-gray-300 dark:text-slate-700",
                      }}
                    />
                  </Popover.Panel>
                </Transition>
              </>
            )}
          </Popover>

          <p className="mt-1 text-xs text-gray-400 dark:text-slate-500">
            {isEdit
              ? "โหมดแก้ไข: เปลี่ยนวันที่ได้ และระบบจะเช็คเวลาซ้ำของวันใหม่ให้"
              : "เลือกวันที่สำหรับการจอง"}
          </p>
        </div>

        {/* Time */}
        <div className="grid grid-cols-1 gap-4 2xl:grid-cols-2">
          <TimeSlotPicker
            label="เวลาเริ่ม (30 นาที)"
            value={startTime}
            onChange={setStartTime}
            slots={startSlots}
            hint="เลือกเป็นช่วงเวลา 30 นาที"
          />
          <TimeSlotPicker
            label="เวลาสิ้นสุด (30 นาที)"
            value={endTime}
            onChange={setEndTime}
            slots={endSlots}
            hint="จะแสดงเฉพาะเวลาที่มากกว่าเวลาเริ่ม"
          />
        </div>

        {/* Duration */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-gray-500 dark:text-slate-400 mr-1">
            ระยะเวลา:
          </span>
          {durations.map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => applyDuration(m)}
              className="px-3 py-1.5 rounded-full text-xs border border-gray-200 dark:border-slate-800 bg-white dark:bg-slate-950 text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-900 transition"
            >
              {m} นาที
            </button>
          ))}
        </div>

        {/* Meeting Topic */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">
            หัวข้อการประชุม
          </label>

          <input
            type="text"
            name="meetingTopic"
            value={purpose}
            onChange={(e) => setPurpose(e.target.value)}
            placeholder="เช่น Weekly Sync, Client Meeting, Review QT/PR/BA"
            maxLength={120}
            onKeyDown={(e) => {
              if (e.key === "Enter") e.preventDefault();
            }}
            className={cx(
              "w-full rounded-xl px-3 py-3",
              "border border-gray-200 dark:border-slate-800",
              "bg-white dark:bg-slate-950",
              "text-gray-800 dark:text-slate-100",
              "placeholder:text-gray-400 dark:placeholder:text-slate-500",
              "focus:outline-none focus:ring-2 focus:ring-indigo-500/70 dark:focus:ring-indigo-400/60",
            )}
          />

          <p className="mt-1 text-xs text-gray-400 dark:text-slate-500">
            ระบุหัวข้อหลักของการประชุม (ไม่เกิน 1 บรรทัด)
          </p>
        </div>

        <div className="flex items-center justify-end gap-3 pt-1">
          {/* ✅ ปุ่มยกเลิกตอนแก้ไข */}
          {isEdit && (
            <button
              type="button"
              onClick={onCancelEdit}
              className="px-5 py-3 rounded-xl border border-gray-200 dark:border-slate-800
                         bg-white dark:bg-slate-950 text-gray-700 dark:text-slate-200
                         hover:bg-gray-50 dark:hover:bg-slate-900 transition
                         focus:outline-none focus:ring-2 focus:ring-gray-300/60 dark:focus:ring-slate-700"
            >
              <span className="inline-flex items-center gap-2">
                <FiX />
                ยกเลิก
              </span>
            </button>
          )}

          <button
            type="submit"
            className="bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-400 text-white px-6 py-3 rounded-xl transition font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500/70 dark:focus:ring-indigo-300/60"
          >
            {isEdit ? "บันทึกการแก้ไข" : "บันทึกการจอง"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default BookingForm;
